import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import useAuthentication from "../../hooks/useAuthentication";
import { useMutation } from "@tanstack/react-query";
import {
  login,
  googleAuthInitiate,
} from "../../utility/httpActions/authenticationActions";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { getRedirectRouteWithFallback } from "../../utility/utilityFunctions";
import TextInput from "../../common-components/TextInput";
import NgwaahiaIcon from "../../common-components/NgwaahiaIcon";
import AppRedirectModal from "../../common-components/AppRedirectModal";
import { trackEvent } from "../../utility/analytics";
import { APP_SCHEME, countryOptions } from "../../utility/constants";
import { getSupportedVisitorCountry } from "../../utility/geo";
import { getLoginHints, saveLoginHints } from "../../utility/tokenUtils";
import CountryPhoneInput, {
  validatePhoneForCountry,
} from "../../common-components/PhoneNumberInput/CountryPhoneInput";

// Sign up only happens in the app. When the user came from a shop or product,
// open the app on that same page so they land back there after signing up.
// Only paths the app's linking config maps (shop/:shopId, product/:productId).
const getSignUpDeepLink = (location) => {
  const from = location.state?.from?.pathname;
  return from && /^\/(shop|product)\/[^/]+$/.test(from)
    ? `${APP_SCHEME}:/${from}`
    : undefined;
};

// Prefill the last phone login on this browser (like mobile's login hints).
// Otherwise pre-select the visitor's IP country (Nigeria if unsupported).
const getDefaultValues = () => {
  const hints = getLoginHints();
  const hintCountry = countryOptions.some((c) => c.value === hints.country)
    ? hints.country
    : null;
  return {
    phoneNumber: hints.phoneNumber || "",
    password: "",
    country: hintCountry || getSupportedVisitorCountry("NG"),
  };
};

const Login = () => {
  const { handleSubmit, control, trigger, getValues } = useForm({
    defaultValues: getDefaultValues(),
  });

  const location = useLocation();
  const { onLogin, isLoggingIn } = useAuthentication();
  const [signUpModalOpen, setSignUpModalOpen] = useState(false);
  const [googleNewUserOpen, setGoogleNewUserOpen] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [applePasswordModalOpen, setApplePasswordModalOpen] = useState(false);
  const signUpDeepLink = getSignUpDeepLink(location);

  const {
    mutate: loginRequest,
    isPending,
    isError,
  } = useMutation({
    mutationFn: login,
    onSuccess: (response) => {
      const { phoneNumber, country } = getValues();
      saveLoginHints(phoneNumber, country);
      trackEvent("user_logged_in", { method: "phone" });
      onLogin?.(response.user, getRedirectRouteWithFallback(location));
    },
  });

  const { mutate: googleLogin, isPending: isGooglePending } = useMutation({
    mutationFn: googleAuthInitiate,
    onSuccess: (response) => {
      if (response.status === "LOGGED_IN") {
        trackEvent("user_logged_in", { method: "google" });
        onLogin?.(response.user, getRedirectRouteWithFallback(location));
      } else if (response.status === "NEW_USER") {
        setGoogleNewUserOpen(true);
      } else if (response.status === "EMAIL_CONFLICT") {
        setGoogleError(response.message);
      }
    },
    onError: () => {
      setGoogleError("Google sign-in failed. Please try again.");
    },
  });

  if (isLoggingIn) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#046287" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 60px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 6,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(loginRequest)}
        sx={{
          width: "100%",
          maxWidth: 420,
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          bgcolor: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <NgwaahiaIcon size={40} color="#046287" />
        </Box>

        <Typography
          variant="h6"
          fontWeight={700}
          textAlign="center"
          sx={{ mb: 0.5 }}
        >
          Welcome back
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 3 }}
        >
          Log in to your Ngwaahia account
        </Typography>

        {isError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Incorrect phone number or password.
          </Alert>
        )}
        {googleError && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setGoogleError(null)}
          >
            {googleError}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <CountryPhoneInput
            control={control}
            rules={{
              required: "Please enter your phone number",
              validate: validatePhoneForCountry,
            }}
            onChangeAfter={() => trigger("phoneNumber")}
          />
          <TextInput
            label="Password"
            type="password"
            name="password"
            control={control}
            rules={{ required: "Please enter your password" }}
          />
        </Box>

        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          disabled={isPending || isGooglePending}
          sx={{
            mt: 2.5,
            bgcolor: "#046287",
            "&:hover": { bgcolor: "#035070" },
            borderRadius: 2,
            fontWeight: 700,
            py: 1.5,
          }}
        >
          {isPending ? (
            <CircularProgress size={22} sx={{ color: "white" }} />
          ) : (
            "Log in"
          )}
        </Button>

        <Divider sx={{ my: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            or
          </Typography>
        </Divider>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pointerEvents: isPending || isGooglePending ? "none" : "auto",
            opacity: isPending || isGooglePending ? 0.6 : 1,
            transition: "opacity 0.15s",
          }}
        >
          {isGooglePending ? (
            <Box
              sx={{
                width: "100%",
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
              }}
            >
              <CircularProgress size={20} sx={{ color: "#046287" }} />
            </Box>
          ) : (
            <GoogleLogin
              onSuccess={(cred) => {
                setGoogleError(null);
                googleLogin(cred.credential);
              }}
              onError={() =>
                setGoogleError("Google sign-in failed. Please try again.")
              }
              theme="outline"
              size="large"
              width="370"
              text="signin_with"
              shape="rectangular"
            />
          )}
        </Box>

        <Box sx={{ textAlign: "center", mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Signed up with Apple on the app?{" "}
            <Typography
              component="span"
              variant="caption"
              fontWeight={600}
              color="#046287"
              sx={{ cursor: "pointer" }}
              onClick={() => setApplePasswordModalOpen(true)}
            >
              Set a password
            </Typography>{" "}
            in the app to log in here.
          </Typography>
        </Box>

        <Box sx={{ textAlign: "center", mt: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            Don't have an account?{" "}
            <Typography
              component="span"
              variant="body2"
              fontWeight={600}
              color="#046287"
              sx={{ cursor: "pointer" }}
              onClick={() => setSignUpModalOpen(true)}
            >
              Sign up in the app
            </Typography>
          </Typography>
        </Box>
      </Box>

      <AppRedirectModal
        open={signUpModalOpen}
        onClose={() => setSignUpModalOpen(false)}
        title="Sign up in the app"
        body="Creating an account, managing your profile, and placing orders all happen in the Ngwaahia app."
        deepLink={signUpDeepLink}
        footer={
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Already have an account?{" "}
            <Typography
              component="span"
              variant="body2"
              fontWeight={600}
              color="#046287"
              sx={{ cursor: "pointer" }}
              onClick={() => setSignUpModalOpen(false)}
            >
              Log in
            </Typography>
          </Typography>
        }
      />
      <AppRedirectModal
        open={googleNewUserOpen}
        onClose={() => setGoogleNewUserOpen(false)}
        title="Finish signing up in the app"
        body="You're almost there! Open the Ngwaahia app to complete your account setup with your phone number."
        deepLink={signUpDeepLink}
      />
      <AppRedirectModal
        open={applePasswordModalOpen}
        onClose={() => setApplePasswordModalOpen(false)}
        title="Set a password in the app"
        body="If you signed up with Apple, you'll need to set a password in the app before you can log in here. Open the app and go to Account Settings to set one."
        deepLink={`${APP_SCHEME}://user/settings/set-password`}
      />
    </Box>
  );
};

export default Login;
